export const ASSET_CATEGORIES = {
    PLANT: 'PLANT',
    EB: 'EB',
    GENERATOR: 'GENERATOR',
    LOADER: 'LOADER',
    TOWING: 'TOWING',
    TRANSIT_MIXER: 'TRANSIT_MIXER',
    PUMP: 'PUMP',
};

export const ASSET_SECTIONS = [
    { key: ASSET_CATEGORIES.PLANT, label: 'Plant', singleRow: true },
    { key: ASSET_CATEGORIES.EB, label: 'EB (Electricity)', singleRow: true },
    { key: ASSET_CATEGORIES.GENERATOR, label: 'Generator', equipType: 'GENERATOR' },
    { key: ASSET_CATEGORIES.LOADER, label: 'Loader (JCB)', equipType: 'LOADER' },
    { key: ASSET_CATEGORIES.TOWING, label: 'Towing Vehicle', equipType: 'TOWING VEHICLE' },
    { key: ASSET_CATEGORIES.TRANSIT_MIXER, label: 'Transit Mixer', equipType: 'TRANSIT MIXER', transactionTable: true },
    { key: ASSET_CATEGORIES.PUMP, label: 'Pump', equipType: 'PUMP', transactionTable: true },
];

export const COST_TYPES = [
    { value: 'EB', label: 'EB Bill' },
    { value: 'PUMP', label: 'Pump Cost' },
    { value: 'GENERATOR', label: 'Generator' },
    { value: 'LOADER', label: 'Loader' },
    { value: 'OTHER', label: 'Other' },
];

export const ALLOCATION_METHODS = [
    { value: 'BY_KWH', label: 'By kWh' },
    { value: 'BY_PRODUCTION', label: 'By Production m³' },
    { value: 'BY_HOURS', label: 'By Hours' },
    { value: 'BY_PUMP_HOURS', label: 'By Pump Hours' },
    { value: 'FLAT_DAILY', label: 'Flat Daily' },
];

export function emptyAssetLine(category, lineNo = 1) {
    return {
        asset_category: category,
        equipment_id: '',
        equipment_name: '',
        line_no: lineNo,
        production_qty: 0,
        opening_km: 0,
        closing_km: 0,
        total_km: 0,
        opening_hm: 0,
        closing_hm: 0,
        running_hm: 0,
        opening_kwh: 0,
        closing_kwh: 0,
        units_kwh: 0,
        diesel_litres: 0,
        pump_qty: 0,
        breakdown_hours: 0,
        idle_hours: 0,
        source: 'MANUAL',
        user_remarks: '',
        gate_pass_id: '',
        work_schedule_id: '',
        dc_no: '',
        dc_time: '',
        plant_name: '',
        customer_name: '',
        site_name: '',
        grade_name: '',
        delivery_mode: '',
        driver_name: '',
        invoice_status: '',
    };
}

export function parseGatePassRef(line) {
    if (line?.gate_pass_id) {
        return String(line.gate_pass_id);
    }
    const match = (line?.user_remarks || '').match(/^\[GP:(\d+)\]/);
    return match ? match[1] : '';
}

export function parseWorkScheduleRef(line) {
    if (line?.work_schedule_id) {
        return String(line.work_schedule_id);
    }
    const match = (line?.user_remarks || '').match(/^\[WS:(\d+)\]/);
    return match ? match[1] : '';
}

export function hasTransactionRef(line) {
    return Boolean(parseGatePassRef(line) || parseWorkScheduleRef(line));
}

export function stripLineRef(remarks = '') {
    return (remarks || '').replace(/^\[(GP|WS):\d+\]\s*/, '').trim();
}

export function stripGatePassRef(remarks = '') {
    return stripLineRef(remarks);
}

function preserveTransactionOverrides(existing, incoming, qtyField) {
    const next = { ...incoming };
    const meterFields = [
        'opening_km', 'closing_km', 'total_km', 'opening_hm', 'closing_hm',
        'running_hm', qtyField,
    ];
    meterFields.forEach((field) => {
        const existingVal = Number(existing[field]);
        const incomingVal = Number(incoming[field]);
        if (existingVal > 0 && incomingVal === 0) {
            next[field] = existing[field];
        } else if (existingVal > 0 && incomingVal > 0 && existingVal !== incomingVal) {
            next[field] = existing[field];
        } else {
            next[field] = incoming[field];
        }
    });
    next.breakdown_hours = existing.breakdown_hours ?? incoming.breakdown_hours ?? 0;
    next.idle_hours = existing.idle_hours ?? incoming.idle_hours ?? 0;
    next.user_remarks = stripLineRef(existing.user_remarks) || incoming.user_remarks || '';
    return next;
}

function mergeCategoryTransactionSuggestions(existingLines, suggestions, category, refKey, qtyField) {
    const others = existingLines.filter((line) => line.asset_category !== category);
    const existingCategory = existingLines.filter((line) => line.asset_category === category);
    const manualLines = existingCategory.filter(
        (line) => line.source === 'MANUAL' && !(
            refKey === 'gate_pass_id' ? parseGatePassRef(line) : parseWorkScheduleRef(line)
        ),
    );
    const mergedAuto = suggestions.map((suggestion) => {
        const refId = String(suggestion[refKey] || '');
        const existing = existingCategory.find((line) => (
            refKey === 'gate_pass_id'
                ? parseGatePassRef(line) === refId
                : parseWorkScheduleRef(line) === refId
        ));
        const computed = computeDerivedLine({ ...emptyAssetLine(category), ...suggestion });
        return existing ? preserveTransactionOverrides(existing, computed, qtyField) : computed;
    });
    return [...others, ...manualLines, ...mergedAuto].map((line, index) => ({
        ...line,
        line_no: line.line_no || index + 1,
    }));
}

function mergeTransitMixerSuggestions(existingLines, suggestions) {
    return mergeCategoryTransactionSuggestions(
        existingLines,
        suggestions,
        ASSET_CATEGORIES.TRANSIT_MIXER,
        'gate_pass_id',
        'production_qty',
    );
}

function mergePumpSuggestions(existingLines, suggestions) {
    return mergeCategoryTransactionSuggestions(
        existingLines,
        suggestions,
        ASSET_CATEGORIES.PUMP,
        'work_schedule_id',
        'pump_qty',
    );
}

export function buildDefaultDetailList() {
    return [
        emptyAssetLine(ASSET_CATEGORIES.PLANT, 1),
        emptyAssetLine(ASSET_CATEGORIES.EB, 1),
    ];
}

export function computeDerivedLine(line) {
    const next = { ...line };
    const openingKm = Number(next.opening_km) || 0;
    const closingKm = Number(next.closing_km) || 0;
    const openingHm = Number(next.opening_hm) || 0;
    const closingHm = Number(next.closing_hm) || 0;
    const openingKwh = Number(next.opening_kwh) || 0;
    const closingKwh = Number(next.closing_kwh) || 0;

    if (!next.total_km && closingKm > openingKm) {
        next.total_km = Number((closingKm - openingKm).toFixed(3));
    }
    if (!next.running_hm && closingHm > openingHm) {
        next.running_hm = Number((closingHm - openingHm).toFixed(3));
    }
    if (!next.units_kwh && closingKwh > openingKwh) {
        next.units_kwh = Number((closingKwh - openingKwh).toFixed(3));
    }
    return next;
}

export function mergeAutofillSuggestions(existingLines, suggestions) {
    const transitSuggestions = suggestions.filter((s) => s.asset_category === ASSET_CATEGORIES.TRANSIT_MIXER);
    const pumpSuggestions = suggestions.filter((s) => s.asset_category === ASSET_CATEGORIES.PUMP);
    const otherSuggestions = suggestions.filter((s) => (
        s.asset_category !== ASSET_CATEGORIES.TRANSIT_MIXER
        && s.asset_category !== ASSET_CATEGORIES.PUMP
    ));
    let merged = [...existingLines];

    if (transitSuggestions.length > 0) {
        merged = mergeTransitMixerSuggestions(merged, transitSuggestions);
    }
    if (pumpSuggestions.length > 0) {
        merged = mergePumpSuggestions(merged, pumpSuggestions);
    }

    otherSuggestions.forEach((suggestion) => {
        const category = suggestion.asset_category;
        const section = ASSET_SECTIONS.find((s) => s.key === category);
        const existingIndex = merged.findIndex(
            (line) => line.asset_category === category
                && (!section?.singleRow || line.line_no === (suggestion.line_no || 1)),
        );

        const computed = computeDerivedLine({ ...emptyAssetLine(category), ...suggestion });
        if (existingIndex >= 0 && section?.singleRow) {
            merged[existingIndex] = { ...merged[existingIndex], ...computed };
        } else if (existingIndex >= 0 && suggestion.equipment_id) {
            const equipIndex = merged.findIndex(
                (line) => line.asset_category === category
                    && String(line.equipment_id) === String(suggestion.equipment_id),
            );
            if (equipIndex >= 0) {
                merged[equipIndex] = { ...merged[equipIndex], ...computed };
            } else {
                merged.push(computed);
            }
        } else {
            merged.push(computed);
        }
    });
    return merged.map((line, index) => ({ ...line, line_no: line.line_no || index + 1 }));
}

export function groupLinesByCategory(detailList) {
    const grouped = {};
    ASSET_SECTIONS.forEach((section) => {
        grouped[section.key] = detailList.filter((line) => line.asset_category === section.key);
    });
    return grouped;
}

export function flattenGroupedLines(grouped) {
    const lines = [];
    ASSET_SECTIONS.forEach((section) => {
        const sectionLines = grouped[section.key] || [];
        if (sectionLines.length === 0 && section.singleRow) {
            lines.push(emptyAssetLine(section.key, 1));
        } else {
            sectionLines.forEach((line, index) => {
                lines.push({ ...computeDerivedLine(line), line_no: index + 1 });
            });
        }
    });
    return lines.filter((line) => {
        if (hasTransactionRef(line)) {
            return true;
        }
        const values = [
            line.production_qty, line.opening_km, line.closing_km, line.total_km,
            line.opening_hm, line.closing_hm, line.running_hm, line.opening_kwh,
            line.closing_kwh, line.units_kwh, line.diesel_litres, line.pump_qty,
            line.breakdown_hours, line.idle_hours, line.equipment_id,
        ];
        return values.some((value) => Number(value) > 0) || (line.user_remarks || '').trim();
    });
}
